import React, { useEffect, useState } from "react";
import { Spinner } from "./ui";
import { ArgentinaMap } from "./ArgentinaMap";

const Kpi = ({ label, value, accent, testid }) => (
  <div className="bg-white p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-headings">{label}</div>
    <div className={`mt-1 font-headings text-3xl font-bold tabular-nums ${accent ? "text-[#D97706]" : "text-[#0A192F]"}`}>{value}</div>
  </div>
);

const Bars = ({ rows, testid }) => {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.envios, r.descargas)));
  return (
    <div className="flex items-end gap-1.5 h-40 overflow-x-auto" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.periodo} className="flex flex-col items-center gap-1 min-w-[34px] flex-1">
          <div className="flex items-end gap-0.5 h-32">
            <div className="w-3 bg-[#0A2463]" style={{ height: `${(r.envios / max) * 100}%` }} title={`${r.envios} envíos`} />
            <div className="w-3 bg-[#F5B400]" style={{ height: `${(r.descargas / max) * 100}%` }} title={`${r.descargas} descargas`} />
          </div>
          <div className="text-[9px] text-gray-500 whitespace-nowrap">{r.periodo.slice(-3)}</div>
        </div>
      ))}
    </div>
  );
};

export const useStats = (client, refreshKey) => {
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/stats").then((r) => setS(r.data)).catch(() => {}); }, [client, refreshKey]);
  return s;
};

export const KpiGrid = ({ s }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200" data-testid="red-kpis">
    <Kpi label="Contenidos en biblioteca" value={s.total_contenidos} testid="kpi-contenidos" />
    <Kpi label="Videos pendientes de revisión" value={s.videos_pendientes} accent testid="kpi-pendientes" />
    <Kpi label="Videos aprobados" value={s.videos_aprobados} testid="kpi-aprobados" />
    <Kpi label="Descargas de archivos" value={s.total_descargas} testid="kpi-descargas" />
    <Kpi label="Colaboradores registrados" value={s.colaboradores_registrados} testid="kpi-registrados" />
    <Kpi label="Colaboradores activos (30 días)" value={s.colaboradores_activos} testid="kpi-activos" />
    <Kpi label="Envíos con correcciones" value={s.envios.correcciones} testid="kpi-correcciones" />
    <Kpi label="Visitas de la gira" value={s.gira_visitas} testid="kpi-gira" />
  </div>
);

export const Stats = ({ client }) => {
  const s = useStats(client, 0);
  const [periodo, setPeriodo] = useState("semanal");
  if (!s) return <Spinner />;
  return (
    <div className="space-y-6" data-testid="red-stats">
      <KpiGrid s={s} />
      <p className="text-xs text-gray-500">Las descargas cuentan cuántas veces se bajó un archivo desde la biblioteca. No equivalen a visualizaciones ni miden el alcance en redes sociales.</p>
      <div className="bg-white border border-gray-200 p-5">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <h3 className="font-headings uppercase tracking-wide text-[#0A192F]">Actividad</h3>
          <div className="flex gap-1">
            {["semanal", "mensual"].map((p) => (
              <button key={p} onClick={() => setPeriodo(p)} className={`px-3 py-1 text-xs font-headings uppercase border ${periodo === p ? "bg-[#0A2463] text-white border-[#0A2463]" : "border-gray-300"}`} data-testid={`stats-${p}`}>{p}</button>
            ))}
          </div>
        </div>
        <Bars rows={s[periodo]} testid="stats-bars" />
        <div className="flex gap-4 text-xs mt-2"><span className="flex items-center gap-1"><i className="w-3 h-3 bg-[#0A2463] inline-block" /> Envíos recibidos</span><span className="flex items-center gap-1"><i className="w-3 h-3 bg-[#F5B400] inline-block" /> Descargas</span></div>
      </div>
      <ArgentinaMap data={s.aportes_por_provincia} />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 p-5" data-testid="stats-provincias">
          <h3 className="font-headings uppercase tracking-wide text-[#0A192F] mb-3">Aportes por provincia</h3>
          {s.aportes_por_provincia.length === 0 ? <p className="text-sm text-gray-400">Sin envíos todavía.</p> : (
            <table className="w-full text-sm"><thead className="text-[10px] uppercase text-gray-500"><tr><th className="text-left py-1">Provincia</th><th>Envíos</th><th>Aprobados</th></tr></thead>
              <tbody>{s.aportes_por_provincia.map((p) => <tr key={p.provincia} className="border-t"><td className="py-1.5">{p.provincia}</td><td className="text-center">{p.envios}</td><td className="text-center">{p.aprobados}</td></tr>)}</tbody></table>
          )}
        </div>
        <div className="bg-white border border-gray-200 p-5" data-testid="stats-descargas">
          <h3 className="font-headings uppercase tracking-wide text-[#0A192F] mb-3">Descargas por archivo</h3>
          {s.descargas_por_archivo.length === 0 ? <p className="text-sm text-gray-400">Sin descargas todavía.</p> : (
            <table className="w-full text-sm"><tbody>{s.descargas_por_archivo.map((d) => <tr key={d.id} className="border-t"><td className="py-1.5 pr-2">{d.titulo}</td><td className="text-xs text-gray-500">{d.tipo}</td><td className="text-right font-semibold tabular-nums">{d.descargas}</td></tr>)}</tbody></table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Stats;
